import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Printer,
  Signpost,
  Palette,
  Gift,
  Shirt,
  Briefcase,
  Code2,
  ArrowUpRight,
  ArrowRight,
  MessageSquare,
  Clock,
  Factory,
  Truck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import Magnetic from '../components/Magnetic';
import ResponsiveImage from '../components/ResponsiveImage';
import Carousel from '../components/Carousel';
import { EASE, ParallaxImg, RevealWord, fadeUp } from '../components/motion';

/* ------------------------------------------------------------------- data */

const services = [
  {
    number: '01',
    title: 'Premium Printing',
    href: '/services/printing',
    icon: Printer,
    image: '/images/hero/large-format-printing.webp',
    imageMobile: '/images/hero/large-format-printing-mobile.webp',
    subtitle: 'Banners · Posters · PVC · Billboards',
    tag: 'Most requested',
  },
  {
    number: '02',
    title: 'Vehicle Wraps & Signage',
    href: '/services/vehicle-wraps',
    icon: Signpost,
    image: '/images/hero/vehicle-wrapping.webp',
    imageMobile: '/images/hero/vehicle-wrapping-mobile.webp',
    subtitle: 'Full & partial wraps · Shopfronts · Wayfinding',
  },
  {
    number: '03',
    title: 'Branding & Identity',
    href: '/services/branding',
    icon: Palette,
    image: '/images/hero/office-branding.webp',
    imageMobile: '/images/hero/office-branding-mobile.webp',
    subtitle: 'Logos · Brand systems · Packaging',
  },
  {
    number: '04',
    title: 'Digital Services',
    href: '/services/digital-services',
    icon: Code2,
    image: '/images/portfolio-work/getworth.webp',
    imageMobile: '/images/portfolio-work/getworth.webp',
    subtitle: 'Web design · App development · Automation',
    tag: 'New',
  },
  {
    number: '05',
    title: 'Corporate Stationery & Gifts',
    href: '/services/corporate-stationery',
    icon: Gift,
    image: '/images/hero/print-marketing.webp',
    imageMobile: '/images/hero/print-marketing-mobile.webp',
    subtitle: 'Business cards · Letterheads · Branded gifts',
  },
  {
    number: '06',
    title: 'Apparel Printing',
    href: '/services/apparel-printing',
    icon: Shirt,
    image: '/images/hero/promotional-products.webp',
    imageMobile: '/images/hero/promotional-products-mobile.webp',
    subtitle: 'T-shirts · Workwear · Event apparel',
  },
  {
    number: '07',
    title: 'Company Registration & Compliance',
    href: '/services/company-registration',
    icon: Briefcase,
    image: '/images/hero/business-signage.webp',
    imageMobile: '/images/hero/business-signage-mobile.webp',
    subtitle: 'CIPC · Tax clearance · BEE certificates',
    tag: 'Paperwork handled',
  },
];

const process = [
  {
    step: 'STEP 01',
    icon: MessageSquare,
    title: 'Brief us',
    description:
      'WhatsApp, email or walk into Shop 23B at the Mall of Mthatha. Tell us what you need.',
  },
  {
    step: 'STEP 02',
    icon: Clock,
    title: 'Quote in 24h',
    description:
      'A clear, itemised quote in your inbox within one working day. No surprises later.',
  },
  {
    step: 'STEP 03',
    icon: Factory,
    title: 'Production',
    description: 'Design approved, then printed in-house on professional-grade equipment.',
  },
  {
    step: 'STEP 04',
    icon: Truck,
    title: 'Delivery',
    description: 'Collect in Mthatha or have it couriered anywhere in South Africa.',
  },
];

const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'OfferCatalog',
  name: 'DKSN Business Solutions — Services',
  url: 'https://dksnbsolutions.co.za/services',
  itemListElement: services.map((s, i) => ({
    '@type': 'Offer',
    position: i + 1,
    itemOffered: {
      '@type': 'Service',
      name: s.title,
      description: s.subtitle,
      provider: { '@id': 'https://dksnbsolutions.co.za' },
      areaServed: { '@type': 'City', name: 'Mthatha', containedIn: 'Eastern Cape' },
    },
  })),
};

/* ------------------------------------------------------------------- page */

const Services = () => {
  return (
    <>
      <SEO
        title="Business Services in Mthatha — Printing, Branding & Registration"
        description="All the services your business needs under one roof at the Mall of Mthatha: large-format printing, branding and logo design, and CIPC company registration. Serving the whole Eastern Cape."
        path="/services"
        image="/images/hero/large-format-printing.webp"
        imageAlt="Large-format printing and signage by DKSN Business Solutions"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      </Helmet>
      <div className="min-h-screen overflow-hidden bg-primary-950 pt-20 text-white">
        {/* ================================== HERO — compact typographic */}
        <section className="relative overflow-hidden pb-16 pt-20 sm:pb-20 sm:pt-28">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_500px_at_80%_0%,rgba(91,141,239,0.14),transparent_60%)]" />
          <div className="section-container relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: EASE }}
              className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/[0.12] bg-white/5 py-2 pl-3 pr-4"
            >
              <span className="h-[7px] w-[7px] rounded-full bg-azure-400 shadow-[0_0_12px_3px_rgba(110,155,242,0.9)]" />
              <span className="text-[13px] font-medium tracking-wide text-gray-200">
                Quotes within 24 hours
              </span>
            </motion.div>

            <h1 className="mb-7 max-w-5xl font-display text-6xl font-bold leading-[0.98] tracking-[-0.045em] sm:text-7xl lg:text-8xl">
              <RevealWord delay={0.12}>Seven ways we make</RevealWord>
              <br />
              <RevealWord delay={0.26}>you&nbsp;</RevealWord>
              <RevealWord delay={0.26} className="font-serif font-normal italic text-azure-300">
                impossible to ignore.
              </RevealWord>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.55, ease: EASE }}
              className="max-w-lg text-lg leading-relaxed text-gray-400"
            >
              Hover a service, see the work. Every line below ends in the same place — a quote in
              your inbox within a day.
            </motion.p>
          </div>
        </section>

        {/* =============================================== SERVICE INDEX */}
        <section className="section-container pb-10">
          {services.map((service) => (
            <motion.div
              key={service.number}
              {...fadeUp}
              className="group relative border-t border-white/[0.09] last:border-b"
            >
              <Link
                to={service.href}
                className="grid grid-cols-[56px_1fr] items-center gap-6 py-8 transition-[padding] duration-400 ease-out hover:pl-3 sm:grid-cols-[90px_1fr_auto_auto] sm:gap-8 sm:py-9"
              >
                <span className="font-display text-[15px] font-semibold tracking-[0.1em] text-azure-500">
                  {service.number}
                </span>

                <span>
                  <span className="block font-display text-3xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-4xl lg:text-[46px]">
                    {service.title}
                  </span>
                  <span className="mt-2 block text-[14.5px] text-gray-500">
                    {service.subtitle}
                  </span>
                </span>

                {service.tag ? (
                  <span className="hidden whitespace-nowrap rounded-full border border-white/[0.12] px-4 py-2 text-[13px] font-medium text-gray-300 sm:inline-block">
                    {service.tag}
                  </span>
                ) : (
                  <span className="hidden sm:inline-block" />
                )}

                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/[0.15] transition-all duration-300 ease-out group-hover:rotate-45 group-hover:border-azure-500 group-hover:bg-azure-500 sm:h-[52px] sm:w-[52px]">
                  <ArrowUpRight className="h-5 w-5" />
                </span>
              </Link>

              {/* hover preview thumbnail */}
              <div className="pointer-events-none absolute right-8 top-1/2 hidden aspect-[4/3] w-[280px] -translate-y-1/2 translate-x-3 rotate-[-2deg] overflow-hidden rounded-2xl opacity-0 shadow-[0_40px_90px_-20px_rgba(0,0,0,0.9)] transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:rotate-0 group-hover:opacity-100 lg:block">
                <ResponsiveImage
                  desktopSrc={service.image}
                  mobileSrc={service.imageMobile}
                  alt={service.title}
                  className="h-full w-full object-cover"
                />
              </div>
            </motion.div>
          ))}
        </section>

        {/* ======================================================= PROCESS */}
        <section className="section-container pt-16 sm:pt-36">
          <motion.div {...fadeUp} className="mb-16">
            <h2 className="max-w-3xl font-display text-4xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">
              From brief to delivery in{' '}
              <span className="font-serif font-normal italic text-azure-300">four steps.</span>
            </h2>
          </motion.div>
          <Carousel slideClassName="w-[74%]" desktopGridClassName="sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-[18px]">
            {process.map((step) => (
              <div key={step.step} className="h-full rounded-[20px] border border-white/[0.09] bg-white/[0.02] p-7">
                <div className="mb-11 flex items-center justify-between">
                  <span className="font-display text-sm font-semibold tracking-[0.1em] text-azure-500">
                    {step.step}
                  </span>
                  <step.icon className="h-5 w-5 text-azure-400" />
                </div>
                <h3 className="mb-2.5 font-display text-xl font-bold tracking-[-0.02em]">
                  {step.title}
                </h3>
                <p className="text-[14.5px] leading-relaxed text-gray-500">{step.description}</p>
              </div>
            ))}
          </Carousel>
        </section>

        {/* =========================================================== CTA */}
        <section className="relative mt-16 overflow-hidden sm:mt-36">
          <ParallaxImg
            src="https://images.unsplash.com/photo-1573164574511-73c773193279?w=2000&auto=format&fit=crop&q=75"
            alt="Planning session"
            speed={18}
            className="absolute inset-0"
            overlayClassName="bg-gradient-to-b from-primary-950 via-primary-950/85 to-primary-950/90"
          />
          <motion.div
            {...fadeUp}
            className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-44"
          >
            <h2 className="mb-5 font-display text-5xl font-bold leading-[1.02] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
              Your quote is{' '}
              <span className="font-serif font-normal italic text-azure-300">
                one message away.
              </span>
            </h2>
            <p className="mx-auto mb-10 max-w-lg text-lg leading-relaxed text-gray-300">
              Send your brief now — we reply within 24 hours, every time.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3.5">
              <Magnetic>
                <Link
                  to="/quote"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-gray-100 px-9 py-[19px] text-base font-semibold text-primary-950 shadow-[0_20px_60px_-18px_rgba(255,255,255,0.55)] transition-colors hover:bg-white"
                >
                  Request a Quote
                  <ArrowRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <a
                href="https://wa.me/27679210514"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-8 py-[19px] text-base font-medium text-white backdrop-blur-md transition-colors hover:bg-white/15"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Services;
